import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { CannedReply } from '@repletoryreach/sdk-api/inbox';
import { Button, Text } from '@repletoryreach/sdk-ui';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';
import { TagChips } from './TagChips';
import { NotesPanel } from './NotesPanel';
import { QuickReplies } from './QuickReplies';
import { ContactContext } from './ContactContext';

export type SidebarSectionId = 'details' | 'contact' | 'notes' | 'replies' | 'shortcuts';

/** Which sections start open, before an agent has chosen for themselves. */
export const DEFAULT_SECTIONS: Record<SidebarSectionId, boolean> = {
  details: true,
  contact: false,
  notes: true,
  replies: true,
  shortcuts: false
};

/** The element id of a section's body, so the page can scroll one into view. */
export const sidebarSectionId = (id: SidebarSectionId) => `rr-inbox-side-${id}`;

/** Key label, then what it does. Both are catalogue keys. */
const SHORTCUTS: { keys: string; action: string }[] = [
  { keys: 'inbox.shortcuts.key.enter', action: 'inbox.shortcuts.send' },
  { keys: 'inbox.shortcuts.key.shiftEnter', action: 'inbox.shortcuts.newline' },
  { keys: 'inbox.shortcuts.key.slash', action: 'inbox.shortcuts.canned' },
  { keys: 'inbox.shortcuts.key.arrows', action: 'inbox.shortcuts.pick' },
  { keys: 'inbox.shortcuts.key.enterTab', action: 'inbox.shortcuts.insert' },
  { keys: 'inbox.shortcuts.key.esc', action: 'inbox.shortcuts.dismiss' }
];

export interface ConversationSidebarProps {
  id?: string;
  conversationId: string;
  /** The number, or on Messenger and Instagram the channel's id for the person. */
  contactRef: string;
  displayName: string | null | undefined;
  assignee: string | null;
  assignData: { value: string; label: string }[];
  onAssign: (userId: string | null) => void;
  /**
   * The thread's team and the organisation's teams (7 October 2026). The picker hides itself when the
   * organisation has no teams, so nothing changes for one that never made any.
   */
  team: string | null;
  teamData: { value: string; label: string }[];
  onTeam: (teamId: string | null) => void;
  teamPending: boolean;
  /** What this contact has cost so far. Null while unknown, and always on a channel with no number. */
  spend: { amount: string; entries: number } | null;
  onInsertCanned: (reply: CannedReply) => void;
  repliesDisabled: boolean;
  sections: Record<SidebarSectionId, boolean>;
  onToggleSection: (id: SidebarSectionId) => void;
  onClose: () => void;
  /** Drop the title bar when a drawer already provides one. */
  hideHeader?: boolean;
  /** Whether the bot is staying out of this thread, and until when (null: until a person hands it back). */
  bot: { paused: boolean; resumesAt: string | null };
  /** Whether Take over / Hand back is offered here: as in the header, not on a closed conversation, a call or an email. */
  botControl: boolean;
  onBotPaused: (paused: boolean) => void;
  botPending: boolean;
  /** Whether the team blocked this customer, since when, why, and whether on WhatsApp too. */
  block: { blocked: boolean; since: string | null; onWhatsapp: boolean; reason: string | null };
  onBlock: () => void;
  onUnblock: () => void;
  blockPending: boolean;
}

/**
 * Conversation settings: everything about a conversation that is not the
 * conversation itself.
 *
 * Laid out after `_UI_improvements/tenant/images/inbox-full-height-sidebar-open.png`.
 * The notes, quick replies and the header's working facts (assignee, tags,
 * spend) used to sit under the composer and across the header, which cost the
 * message history most of its height. They live here now, so the history and
 * the reply box get the whole column.
 *
 * Each section folds on its own and remembers how it was left, because an
 * agent who never uses quick replies should not have to scroll past them on
 * every conversation. A folded section stays mounted, so a half-written note
 * survives being folded away.
 */
export function ConversationSidebar({
  id, conversationId, contactRef, displayName, assignee, assignData, onAssign, team, teamData, onTeam, teamPending, spend,
  onInsertCanned, repliesDisabled, sections, onToggleSection, onClose, hideHeader,
  bot, botControl, onBotPaused, botPending, block, onBlock, onUnblock, blockPending
}: ConversationSidebarProps) {
  const t = useT();

  return (
    <aside
      id={id}
      data-guide="inbox-sidebar"
      className={`rr-inbox2__side${hideHeader ? ' rr-inbox2__side--drawer' : ''}`}
      aria-label={t('inbox.sidebar.title')}
    >
      {!hideHeader && (
        <header className="rr-inbox2__side-head">
          <h2 className="rr-inbox2__side-title">{t('inbox.sidebar.title')}</h2>
          <button
            type="button"
            className="rr-inbox2__details-close"
            onClick={onClose}
            aria-label={t('inbox.sidebar.close')}
          >
            <CloseIcon />
          </button>
        </header>
      )}

      <div className="rr-inbox2__side-body">
        <Section id="details" title={t('inbox.sidebar.details')} icon={<DetailsIcon />} open={sections.details} onToggle={onToggleSection}>
          <Select
            size="sm"
            label={t('inbox.assignedTo.label')}
            value={assignee ?? ''}
            onChange={(e) => onAssign(e.currentTarget.value || null)}
            data={assignData}
          />

          {/* Only the options beyond "No team" mean the organisation has teams. */}
          {teamData.length > 1 && (
            <Select
              size="sm"
              label={t('inbox.team.label')}
              description={t('inbox.team.hint')}
              value={team ?? ''}
              disabled={teamPending}
              onChange={(e) => onTeam(e.currentTarget.value || null)}
              data={teamData}
            />
          )}

          {/* The bot's state on this thread, with the one control that changes
              it. Before this the only way to hand a thread back was to
              resolve it, and nothing said the bot was quiet at all. */}
          <div className="rr-inbox2__side-field">
            <Text role="caption" className="rr-inbox2__side-label">{t('inbox.bot.label')}</Text>
            <div className="rr-inbox2__bot-row">
              <Text role="bodySmall" fw={600}>{bot.paused ? t('inbox.bot.paused') : t('inbox.bot.active')}</Text>
              {botControl && (
                <Button size="xs" variant="default" loading={botPending} onClick={() => onBotPaused(!bot.paused)}>
                  {bot.paused ? t('inbox.takeover.handBack') : t('inbox.takeover.takeOver')}
                </Button>
              )}
            </div>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {bot.paused
                ? bot.resumesAt ? t('inbox.bot.resumesAt', { time: formatDateTime(bot.resumesAt) }) : t('inbox.bot.untilResolved')
                : t('inbox.bot.activeHint')}
            </Text>
          </div>

          {/* Blocking a customer who keeps using language the organisation does
              not accept (15 September 2026). The dialog says what it does. */}
          <div className="rr-inbox2__side-field">
            <Text role="caption" className="rr-inbox2__side-label">{t('inbox.block.label')}</Text>
            <div className="rr-inbox2__bot-row">
              <Text role="bodySmall" fw={600}>{block.blocked ? t('inbox.block.blocked') : t('inbox.block.notBlocked')}</Text>
              <Button size="xs" variant="default" loading={blockPending} onClick={block.blocked ? onUnblock : onBlock}>
                {block.blocked ? t('inbox.block.unblock') : t('inbox.block.action')}
              </Button>
            </div>
            {block.blocked && (
              <Text role="caption" c="var(--rr-color-text-muted)">
                {[
                  block.since ? t('inbox.block.since', { time: formatDateTime(block.since) }) : null,
                  block.onWhatsapp ? t('inbox.block.onWhatsapp') : null,
                  block.reason
                ].filter(Boolean).join(' · ')}
              </Text>
            )}
          </div>

          <div className="rr-inbox2__side-field">
            <Text role="caption" className="rr-inbox2__side-label">{t('inbox.tags')}</Text>
            <TagChips conversationId={conversationId} addClassName="rr-inbox2__tag-add" />
          </div>

          {/* Messages, bot replies and model tokens all carry a price; an agent
              deciding whether to keep the bot on this chat or take over can see
              what it has cost. */}
          {spend && (
            <div className="rr-inbox2__side-field">
              <Text role="caption" className="rr-inbox2__side-label">{t('inbox.spend')}</Text>
              <Link to="/billing/usage" className="rr-inbox2__spend" title={t('inbox.spendHint', { count: spend.entries })}>
                {spend.amount}
              </Link>
            </div>
          )}
        </Section>

        <Section id="contact" title={t('inbox.context.title')} icon={<ContactIcon />} open={sections.contact} onToggle={onToggleSection}>
          <ContactContext conversationId={conversationId} msisdn={contactRef} displayName={displayName} />
        </Section>

        <Section id="notes" title={t('inbox.notes.title')} icon={<NoteIcon />} open={sections.notes} onToggle={onToggleSection}>
          <NotesPanel conversationId={conversationId} />
        </Section>

        <Section id="replies" title={t('inbox.quickReplies.title')} icon={<PenIcon />} open={sections.replies} onToggle={onToggleSection}>
          <QuickReplies onInsert={onInsertCanned} disabled={repliesDisabled} />
        </Section>

        <Section id="shortcuts" title={t('inbox.sidebar.shortcuts')} icon={<KeyboardIcon />} open={sections.shortcuts} onToggle={onToggleSection}>
          <dl className="rr-inbox2__shortcuts">
            {SHORTCUTS.map((s) => (
              <div key={s.action} className="rr-inbox2__shortcut">
                <dt><kbd>{t(s.keys)}</kbd></dt>
                <dd>{t(s.action)}</dd>
              </div>
            ))}
          </dl>
        </Section>
      </div>
    </aside>
  );
}

function Section({ id, title, icon, open, onToggle, children }: {
  id: SidebarSectionId;
  title: string;
  icon: ReactNode;
  open: boolean;
  onToggle: (id: SidebarSectionId) => void;
  children: ReactNode;
}) {
  const bodyId = sidebarSectionId(id);
  return (
    <section className={`rr-inbox2__side-section${open ? ' is-open' : ''}`} data-guide={`inbox-side-${id}`}>
      <h3 className="rr-inbox2__side-section-head">
        <button
          type="button"
          className="rr-inbox2__side-toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => onToggle(id)}
        >
          <span className="rr-inbox2__side-icon" aria-hidden>{icon}</span>
          <span className="rr-inbox2__side-section-title">{title}</span>
          <span className="rr-inbox2__side-chevron" aria-hidden><ChevronIcon /></span>
        </button>
      </h3>
      <div id={bodyId} className="rr-inbox2__side-section-body" hidden={!open}>
        {children}
      </div>
    </section>
  );
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const
};

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" {...stroke} strokeWidth={2} aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
function ChevronIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" {...stroke} strokeWidth={1.8} aria-hidden>
      <polyline points="4,6 8,10 12,6" />
    </svg>
  );
}
function DetailsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" {...stroke} aria-hidden>
      <path d="M4 1.8h6.2L13 4.6v9.6H4z" />
      <path d="M9.9 1.9v3h3" />
      <path d="M6 8h5M6 10.6h3.5" />
    </svg>
  );
}
function ContactIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" {...stroke} aria-hidden>
      <circle cx="8" cy="5.5" r="2.7" />
      <path d="M2.8 14c.6-2.6 2.7-4.2 5.2-4.2s4.6 1.6 5.2 4.2" />
    </svg>
  );
}
function NoteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" {...stroke} aria-hidden>
      <rect x="2.8" y="2" width="10.4" height="12" rx="1.6" />
      <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" />
    </svg>
  );
}
function PenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" {...stroke} aria-hidden>
      <path d="M11.2 2.3 13.7 4.8 5.5 13H3v-2.5z" />
      <path d="M9.8 3.7 12.3 6.2" />
    </svg>
  );
}
function KeyboardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" {...stroke} aria-hidden>
      <rect x="1.5" y="4" width="13" height="8.5" rx="1.6" />
      <path d="M4 6.8h.01M6.5 6.8h.01M9 6.8h.01M11.5 6.8h.01M5 9.8h6" />
    </svg>
  );
}
